import type {
	BacklogDataValidationResult,
	BacklogDataValidationResultError,
} from "../contracts/api/BacklogDataValidationResult";
import type { BacklogDataValidator as BacklogDataValidatorContract } from "../contracts/api/BacklogDataValidator";
import type { BacklogResponse } from "../contracts/api/BacklogResponse";
import type { BacklogEntityMetadata } from "../contracts/utils/metadata/BacklogEntityMetadata";
import type { MatchDetail } from "../contracts/utils/metadata/MatchDetail";
import type { MetaTypeKey } from "../contracts/utils/metadata/MetaTypeKey";
import { assert, resolveBacklogContainer } from "../utils/BacklogUtil";

export class BacklogDataValidator<T extends object>
	implements BacklogDataValidatorContract<T>
{
	constructor(
		private options: {
			response: BacklogResponse<T>;
			validate: {
				entity_type_key: MetaTypeKey<T>;
			};
		},
	) {
		assert(options);
		assert(options.response);
		assert(options.validate);
		assert(options.validate.entity_type_key);
	}

	getEntityMetadata(): BacklogEntityMetadata<T> {
		const meta = resolveBacklogContainer<T>("BacklogMetadata");
		assert(meta);
		const metadata = meta.getEntityMetadata(
			this.options.validate.entity_type_key,
		);
		return metadata;
	}

	async validate(options?: {
		strict: boolean;
	}): Promise<BacklogDataValidationResult> {
		const errors = this._validate(options);
		return {
			isValid: errors.length === 0,
			errors: errors,
		};
	}

	private _validate(options?: {
		strict: boolean;
	}): BacklogDataValidationResultError[] {
		const errors: BacklogDataValidationResultError[] = [];

		const metadata = this.getEntityMetadata();
		const data = this.options.response.data();

		const type_info_list = metadata.getAllTypeInfo();
		if (type_info_list.length === 0) {
			errors.push({
				message: `Entity Metadata Not Found: ${metadata.getTypeName()}`,
			});
		}

		const unset = <T extends object>(arr: T): Partial<T> => {
			const dup: Partial<T> = {};
			(Object.keys(arr) as (keyof Partial<T>)[]).forEach((key, _) => {
				if (Array.isArray(arr[key])) {
					if (arr[key].length === 0) {
						return;
					}
				}
				dup[key] = arr[key];
			});
			return dup;
		};

		const checkMessage = (d: unknown): Partial<MatchDetail>[] => {
			const msgs: MatchDetail[] = [];
			for (const type_info of type_info_list) {
				const c = type_info.matchTypeDetail(d);
				if (options?.strict) {
					if (c.matchStrict) {
						return [];
					}
				} else {
					if (c.match) {
						return [];
					}
				}
				msgs.push(c);
			}
			return msgs
				.map((v) => {
					return {
						extraKeys: v.extraKeys,
						missingKeys: v.missingKeys,
						differenceKeys: v.differenceKeys,
					};
				})
				.map((v) => unset(v));
		};

		if (typeof data === "object") {
			if (metadata.isArrayType()) {
				if (Array.isArray(data)) {
					for (let index = 0; index < data.length; index++) {
						const d = data[index];
						const msgs = checkMessage(d);
						errors.push(
							...msgs.map((v) => {
								return {
									...{ message: `${metadata.getTypeName()}[${index}]` },
									...v,
								};
							}),
						);
					}
				} else {
					errors.push({
						message: `Response Data is expected to be of type "Array", but got "${typeof data}"`,
					});
				}
			} else {
				errors.push(
					...checkMessage(data).map((v) => {
						return {
							...{ message: `${metadata.getTypeName()}` },
							...v,
						};
					}),
				);
			}
		} else {
			errors.push({
				message: `Response Data is expected to be of type "Object", but got "${typeof data}"`,
			});
		}

		return errors;
	}
}
